'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { microcopySetToResourceBundles } from '@lib/coveo/microcopySetToResourceBundles';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { Star } from 'phosphor-icons';
import { useCallback, useMemo } from 'react';
import { TreeAccordion, c } from 'ui-components';
import { FacetAccordion } from '../FacetAccordion';
import { FacetDropdown } from '../FacetDropdown';
import { getRatingController } from '../facetControllers';
import { NumericFlatFacetValue, useFlatFacet } from '../hooks/useFlatFacet';
import { RATING_RANGES } from '../utils/facetRanges';
import { RatingFacetProps } from './RatingFacet.types';

const getValueId = (v: NumericFlatFacetValue): string =>
  `rating-${v.start}-${v.end}`;

export const RatingFacet = ({
  variant = 'horizontal',
  className,
  isOpen,
  onToggle,
}: RatingFacetProps) => {
  const { searchLabelsMicrocopy, searchValuesMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyItem[];
    searchValuesMicrocopy: MicrocopyItem[];
  }>();

  const ratingLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.filter-rating-label')
      ?.value ?? 'Rating';

  const clearLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.clear')?.value ??
    'Clear';

  const ratingsBundle = useMemo(
    () => microcopySetToResourceBundles(searchValuesMicrocopy ?? []).ratings,
    [searchValuesMicrocopy]
  );

  const {
    optimisticValues,
    selectedIds,
    selectedCount,
    handleClear,
    handleSelectMany,
  } = useFlatFacet({
    getController: getRatingController,
    fieldName: 'ec_rating',
    getValueId,
  });

  const getLabel = useCallback(
    (start: number, end: number) => {
      const range = RATING_RANGES.find(
        (r) => r.start === start && r.end === end
      );
      if (!range) return `${start}-${end}`;
      return ratingsBundle?.[range.bundleKey] ?? range.label;
    },
    [ratingsBundle]
  );

  const isDark = variant === 'mobile';

  const treeItems = useMemo(
    () =>
      [...optimisticValues]
        .sort((a, b) => b.start - a.start)
        .map((v) => ({
          id: getValueId(v),
          label: getLabel(v.start, v.end),
          count: v.numberOfResults,
          disabled: v.numberOfResults === 0 && v.state !== 'selected',
          icon: (
            <Star
              size={16}
              weight="fill"
              className={c(
                'shrink-0',
                isDark ? 'text-grey-400' : 'text-grey-700'
              )}
            />
          ),
        })),
    [optimisticValues, getLabel, isDark]
  );

  const content = (
    <TreeAccordion
      items={treeItems}
      selectionMode="multiple"
      selectedIds={selectedIds}
      onSelectMany={handleSelectMany}
      isDarkTheme={isDark}
      size={variant === 'sidebar' ? 'default' : 'sm'}
      reserveExpandSpace={variant !== 'horizontal'}
    />
  );

  if (variant === 'horizontal') {
    return (
      <FacetDropdown
        label={ratingLabel}
        selectedCount={selectedCount}
        clearLabel={clearLabel}
        onClear={handleClear}
        className={className}
      >
        {content}
      </FacetDropdown>
    );
  }

  return (
    <FacetAccordion
      title={ratingLabel}
      variant={variant}
      className={className}
      onClear={handleClear}
      clearLabel={clearLabel}
      showClear={variant === 'drawer' && selectedCount > 0}
      defaultOpen={variant !== 'drawer'}
      selectedCount={selectedCount}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      {content}
    </FacetAccordion>
  );
};
